import cx from 'classnames'
import { Children, useState } from 'react'

import { styles, variantStyles } from './accordion.styles'
import type { AccordionProps } from './accordion.types'
import { AccordionItem } from './item'
import {
  AccordionBusContext,
  createAccordionBus,
} from './item/accordion-group.context'

/**
 * Accordions stack expandable items, each with a title that reveals more
 * content when opened. Use them to shorten long pages whose sections are read
 * selectively, such as FAQs.
 *
 * [View in Figma](https://www.figma.com/design/uk4HuDPdSGgZMSi3iybv5V/Servicing-Landing-Pages?node-id=3109-51886)
 *
 * ## Usage
 *
 * The accordion holds its items. Each `Accordion.Item` takes its header as
 * props, `title` and an optional `iconName`, and its children are the content
 * shown when the item is expanded. `expanded` opens an item when it first
 * renders, `isDisabled` stops its trigger toggling, and `onOpenChange` reports
 * each change the user makes to it.
 *
 * ```tsx
 * import { Accordion } from '@cinch-nx/feature-servicing'
 *
 * export const ServicingFaqs = () => (
 *   <Accordion variant="light-2">
 *     <Accordion.Item title="What's included in a full service?" iconName="shield" expanded>
 *       <p>An oil and filter change, plus checks on brakes and tyres.</p>
 *     </Accordion.Item>
 *     <Accordion.Item title="Can I move my booking?" iconName="calendar">
 *       <p>Yes. Use the link in your confirmation email.</p>
 *     </Accordion.Item>
 *   </Accordion>
 * )
 * ```
 *
 * Opening an item closes the others that are open, so the user only ever opens
 * one at a time, and the open item can be closed so that none are. Several
 * items can start expanded; they stay open until the user opens an item. The
 * header the user tapped stays where it was on screen, even when a panel that
 * closes sat above it. A disabled item keeps its state whatever the others do,
 * so it can stay open next to the open item.
 *
 * There is no size prop. Items use the S (mobile) size below 1024px and the L
 * (desktop) size from 1024px up. At L the title grows from 14px to 18px, the
 * content from 14px / 21px to 16px / 24px, and a one-line header row from
 * 64px to 72px tall.
 *
 * ## Best Practices
 *
 * **Do**:
 * - Write titles that make sense on their own, since most readers only scan
 *   them.
 * - Keep the icon decorative; it is hidden from assistive technology.
 * - Key items built from CMS content by the entry's ID, not its index, so the
 *   same item stays open when entries are added or reordered.
 *
 * **Don't**:
 * - Hide content most readers need; leave it on the page instead.
 * - Put anything other than `Accordion.Item` inside the accordion. Other
 *   children are rendered as they are, without an item's styles. An item
 *   returned by a component of your own, or inside a fragment or array, is
 *   still an item.
 * - Render an `Accordion.Item` outside an accordion. Its colours and sizes come
 *   from the surrounding accordion.
 * - Use `expanded` to open an item after it has rendered. It only sets the
 *   state the item starts in, so later changes to it are ignored.
 */
function AccordionRoot(props: AccordionProps) {
  const { children, variant = 'white', className, ...htmlProps } = props
  // Created once, so its identity never changes and never re-renders the items.
  const [bus] = useState(createAccordionBus)

  if (Children.toArray(children).length === 0) {
    return null
  }

  return (
    <AccordionBusContext.Provider value={bus}>
      <div
        className={cx(styles.base, variantStyles[variant], className)}
        {...htmlProps}
      >
        {children}
      </div>
    </AccordionBusContext.Provider>
  )
}

export const Accordion = Object.assign(AccordionRoot, {
  Item: AccordionItem,
})

export default Accordion
